import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface MapDriver {
  id: string;
  name: string;
  code: string;
  // vehicle: string;
  // vehicleNumber: string;
  image: string;
  latitude: string;
  longitude: string;
  // accuracy?: number;
  speed?: number;
  direction?: number;
  accuracy?: number;
  currentLocation?: string;
  // destination?: string;
  // ordersToday?: number;
  // ordersCompleted?: number;
  // rating?: number;
  online: boolean;
  freshnessStatus?: "LIVE" | "RECENT" | "STALE" | "OFFLINE";
  lastSeen?: string;
  lat: number;
  lon: number;
  // heading: number;
  // route?: { lat: number; lon: number }[];
  totalShipments?: number;
  outForDelivery?: number;
  ordersDelivered?: number;
  ordersNotDelivered?: number;
}

interface DriverMapState {
  drivers: MapDriver[];
  loading: boolean;
  error: string | null;
  socketConnected: boolean;
  lastUpdate: number | null;
}

const initialState: DriverMapState = {
  drivers: [],
  loading: false,
  error: null,
  socketConnected: false,
  lastUpdate: null,
};

const driverMapSlice = createSlice({
  name: 'driverMap',
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },

    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },

    setSocketConnected(state, action: PayloadAction<boolean>) {
      state.socketConnected = action.payload;
    },

    setDrivers(state, action: PayloadAction<MapDriver[]>) {
      state.drivers = action.payload;
      state.loading = false;
      state.error = null;
      state.lastUpdate = Date.now();
    },

    updateDriverLocation(state, action: PayloadAction<Partial<MapDriver> & { id: string }>) {
      const targetId = action.payload.id || (action.payload as any).driverId;
      const targetCode = action.payload.code || (action.payload as any).driverCode;

      const index = state.drivers.findIndex(
        d => (targetId && String(d.id) === String(targetId)) || 
             (targetCode && String(d.code) === String(targetCode))
      );
      
      if (index !== -1) {
        const existing = state.drivers[index];
        const incomingImage = action.payload.image;
        const isHttpUrl = (str: any) =>
          typeof str === "string" && (str.startsWith("http://") || str.startsWith("https://") || str.startsWith("data:"));

        const preservedImage: string =
          (isHttpUrl(incomingImage)
            ? incomingImage
            : isHttpUrl(existing.image)
            ? existing.image
            : (incomingImage || existing.image || "")) || "";

        state.drivers[index] = {
          ...existing,
          ...action.payload,
          id: existing.id,
          image: preservedImage,
          lat: action.payload.lat ?? (action.payload.latitude ? parseFloat(action.payload.latitude) : existing.lat),
          lon: action.payload.lon ?? (action.payload.longitude ? parseFloat(action.payload.longitude) : existing.lon),
        };
      } else {
        state.drivers.push(action.payload as MapDriver);
      }
      
      state.lastUpdate = Date.now();
    },

    updateMultipleDrivers(state, action: PayloadAction<Partial<MapDriver>[]>) {
      action.payload.forEach(update => {
        const targetId = update.id || (update as any).driverId;
        const targetCode = update.code || (update as any).driverCode;
        if (!targetId && !targetCode) return;
        
        const index = state.drivers.findIndex(
          d => (targetId && String(d.id) === String(targetId)) || 
               (targetCode && String(d.code) === String(targetCode))
        );
        
        if (index !== -1) {
          const existing = state.drivers[index];
          const incomingImage = update.image;
          const isHttpUrl = (str: any) =>
            typeof str === "string" && (str.startsWith("http://") || str.startsWith("https://") || str.startsWith("data:"));

          const preservedImage: string =
            (isHttpUrl(incomingImage)
              ? incomingImage
              : isHttpUrl(existing.image)
              ? existing.image
              : (incomingImage || existing.image || "")) || "";

          state.drivers[index] = {
            ...existing,
            ...update,
            id: existing.id,
            image: preservedImage,
            lat: update.lat ?? (update.latitude ? parseFloat(update.latitude) : existing.lat),
            lon: update.lon ?? (update.longitude ? parseFloat(update.longitude) : existing.lon),
          };
        } else {
          state.drivers.push(update as MapDriver);
        }
      });
      
      state.lastUpdate = Date.now();
    },

    setDriverOnlineStatus(state, action: PayloadAction<{ id: string; online: boolean }>) {
      const index = state.drivers.findIndex(d => d.id === action.payload.id);
      if (index !== -1) {
        state.drivers[index].online = action.payload.online;
        state.lastUpdate = Date.now();
      }
    },

    removeOfflineDrivers(state) {
      state.drivers = state.drivers.filter(d => d.online);
      state.lastUpdate = Date.now();
    },

    clearDrivers(state) {
      state.drivers = [];
      state.error = null;
      state.lastUpdate = null;
    },
  },
});

export const {
  setLoading,
  setError,
  setSocketConnected,
  setDrivers,
  updateDriverLocation,
  updateMultipleDrivers,
  setDriverOnlineStatus,
  removeOfflineDrivers,
  clearDrivers,
} = driverMapSlice.actions;

export default driverMapSlice.reducer;
